import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Tooltip } from '@/components/ui/tooltip'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { useDesignVersionToggle } from '@/hooks'

import {
  selectBuildingsForFilter,
  selectActiveBuilding,
} from '../../../reducers/buildingSelectorReducer'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import api from '../../../api/api'
import Loader from '../../Loader/Loader'

const getWidgetLabels = (t) => ({
  buildingImage: t('home-screen:widgetLabels.buildingImage'),
  shortcuts: t('home-screen:widgetLabels.shortcuts'),
  residentTasks: t('home-screen:widgetLabels.residentTasks'),
  upcomingEvents: t('home-screen:widgetLabels.upcomingEvents'),
  announcements: t('home-screen:widgetLabels.announcements'),
  buildingEssentials: t('home-screen:widgetLabels.buildingEssentials'),
  services: t('home-screen:widgetLabels.services'),
  amenities: t('home-screen:widgetLabels.amenities'),
  neighborhoodPerks: t('home-screen:widgetLabels.neighborhoodPerks'),
})

const getShortcutLabels = (t) => ({
  payRent: t('home-screen:shortcutLabels.payRent'),
  maintenanceRequests: t('home-screen:shortcutLabels.maintenanceRequests'),
  bookings: t('home-screen:shortcutLabels.bookings'),
  unlockDoor: t('home-screen:shortcutLabels.unlockDoor'),
  packages: t('home-screen:shortcutLabels.packages'),
  callUs: t('home-screen:shortcutLabels.callUs'),
  emailUs: t('home-screen:shortcutLabels.emailUs'),
  parking: t('home-screen:shortcutLabels.parking'),
  visitors: t('home-screen:shortcutLabels.visitors'),
  essentials: t('home-screen:shortcutLabels.essentials'),
})

const ALL_SHORTCUT_TYPES = [
  'payRent',
  'maintenanceRequests',
  'bookings',
  'unlockDoor',
  'packages',
  'callUs',
  'emailUs',
  'parking',
  'visitors',
  'essentials',
]

const MAX_ENABLED_SHORTCUTS = 4
const getShortcutsCapHint = (t) =>
  t('home-screen:messages.shortcutsCapHint', { max: MAX_ENABLED_SHORTCUTS })

const HomeScreen = ({ buildings, activeBuilding }) => {
  const { t } = useTranslation(['home-screen'])
  const WIDGET_LABELS = getWidgetLabels(t)
  const SHORTCUT_LABELS = getShortcutLabels(t)
  const SHORTCUTS_CAP_HINT = getShortcutsCapHint(t)
  const [config, setConfig] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const { displaySnackbar } = useSnackbar()
  const { buildingId } = useParams()
  const navigate = useNavigate()

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'home-screen',
    screen: 'HomeScreen',
    to: {
      params: { buildingId: buildingId || activeBuilding?.id },
      path: '/dashboard/building-dashboard/home-screen/:buildingId',
    },
    version: 'legacy',
  })

  const _selectedBuilding = useMemo(
    () => buildings.find((building) => building.id === Number(buildingId)),
    [buildings, buildingId],
  )

  const fetchConfig = async () => {
    setIsLoading(true)
    try {
      const response = await api.getHomepageConfig(buildingId)
      setConfig(response.data)
    } catch (error) {
      displaySnackbar(
        error.response?.data?.message || t('home-screen:messages.loadConfigError'),
        true,
      )
    } finally {
      setIsLoading(false)
    }
  }

  const getEnabledWidgets = () => {
    if (!config) return []

    const widgets = []
    if (config.buildingImageEnabled) widgets.push('buildingImage')
    if (config.shortcutsEnabled) widgets.push('shortcuts')
    if (config.upcomingEventsEnabled) widgets.push('upcomingEvents')
    if (config.announcementsEnabled) widgets.push('announcements')
    if (config.buildingEssentialsEnabled) widgets.push('buildingEssentials')
    if (config.servicesEnabled) widgets.push('services')
    if (config.residentTasksEnabled) widgets.push('residentTasks')
    if (config.chatEnabled) widgets.push('chat')
    if (config.amenitiesEnabled) widgets.push('amenities')
    if (config.neighborhoodPerksEnabled) widgets.push('neighborhoodPerks')
    if (config.transportEnabled) widgets.push('transport')

    return widgets
  }

  const handleToggleWidget = async (widget) => {
    const enabledKey = `${widget}Enabled`
    const currentValue = config[enabledKey]
    const newValue = !currentValue

    const updatedConfig = {
      ...config,
      [enabledKey]: newValue,
    }

    let positions = [...(config.positions || [])]

    if (newValue) {
      // If enabling buildingImage, always add it at position 0
      if (widget === 'buildingImage') {
        positions.unshift({
          position: 0,
          widget: widget,
        })
        // Re-index all other positions
        positions = positions.map((p, idx) => ({ ...p, position: idx }))
      } else {
        // For other widgets, add at the end
        positions.push({
          position: positions.length,
          widget: widget,
        })
      }
    } else {
      // Remove the widget and re-index
      positions = positions.filter((p) => p.widget !== widget)
      positions = positions.map((p, idx) => ({ ...p, position: idx }))
    }

    updatedConfig.positions = positions
    setConfig(updatedConfig)
    setIsSaving(true)

    try {
      await saveConfig(updatedConfig)
      displaySnackbar(t('home-screen:messages.widgetUpdated'), false)
    } catch (error) {
      displaySnackbar(
        error.response?.data?.message || t('home-screen:messages.widgetUpdateError'),
        true,
      )
      fetchConfig()
    } finally {
      setIsSaving(false)
    }
  }

  const handleMoveUp = async (currentIndex) => {
    if (currentIndex === 0) return

    const positions = [...(config.positions || [])]

    // Building image is always at position 0, can't move anything above it
    if (positions[0]?.widget === 'buildingImage' && currentIndex === 1) {
      displaySnackbar(t('home-screen:messages.cannotMoveAboveBuildingImage'), true)
      return
    }

    const temp = positions[currentIndex]
    positions[currentIndex] = positions[currentIndex - 1]
    positions[currentIndex - 1] = temp

    const updatedPositions = positions.map((item, index) => ({
      ...item,
      position: index,
    }))

    const updatedConfig = {
      ...config,
      positions: updatedPositions,
    }

    setConfig(updatedConfig)
    setIsSaving(true)

    try {
      await saveConfig(updatedConfig)
      displaySnackbar(t('home-screen:messages.widgetOrderUpdated'), false)
    } catch (error) {
      displaySnackbar(
        error.response?.data?.message || t('home-screen:messages.widgetOrderUpdateError'),
        true,
      )
      fetchConfig()
    } finally {
      setIsSaving(false)
    }
  }

  const handleMoveDown = async (currentIndex) => {
    const positions = [...(config.positions || [])]
    if (currentIndex === positions.length - 1) return

    // Building image is locked at position 0
    if (positions[currentIndex]?.widget === 'buildingImage') {
      displaySnackbar(t('home-screen:messages.buildingImageMustStayAtTop'), true)
      return
    }

    const temp = positions[currentIndex]
    positions[currentIndex] = positions[currentIndex + 1]
    positions[currentIndex + 1] = temp

    const updatedPositions = positions.map((item, index) => ({
      ...item,
      position: index,
    }))

    const updatedConfig = {
      ...config,
      positions: updatedPositions,
    }

    setConfig(updatedConfig)
    setIsSaving(true)

    try {
      await saveConfig(updatedConfig)
      displaySnackbar(t('home-screen:messages.widgetOrderUpdated'), false)
    } catch (error) {
      displaySnackbar(
        error.response?.data?.message || t('home-screen:messages.widgetOrderUpdateError'),
        true,
      )
      fetchConfig()
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleShortcut = async (shortcutType) => {
    const shortcuts = [...(config.shortcuts || [])]
    const existingIndex = shortcuts.findIndex((s) => s.type === shortcutType)

    let updatedShortcuts
    if (existingIndex >= 0) {
      // Remove shortcut
      updatedShortcuts = shortcuts.filter((s) => s.type !== shortcutType)
      // Re-index positions
      updatedShortcuts = updatedShortcuts.map((s, idx) => ({ ...s, position: idx }))
    } else {
      if (shortcuts.length >= MAX_ENABLED_SHORTCUTS) {
        displaySnackbar(SHORTCUTS_CAP_HINT, true)
        return
      }
      // Add shortcut
      updatedShortcuts = [
        ...shortcuts,
        {
          type: shortcutType,
          position: shortcuts.length,
        },
      ]
    }

    const updatedConfig = {
      ...config,
      shortcuts: updatedShortcuts,
    }

    setConfig(updatedConfig)
    setIsSaving(true)

    try {
      await saveConfig(updatedConfig)
      displaySnackbar(t('home-screen:messages.shortcutUpdated'), false)
    } catch (error) {
      displaySnackbar(
        error.response?.data?.message || t('home-screen:messages.shortcutUpdateError'),
        true,
      )
      fetchConfig()
    } finally {
      setIsSaving(false)
    }
  }

  const handleMoveShortcutUp = async (currentIndex) => {
    if (currentIndex === 0) return

    const shortcuts = [...(config.shortcuts || [])]
    const temp = shortcuts[currentIndex]
    shortcuts[currentIndex] = shortcuts[currentIndex - 1]
    shortcuts[currentIndex - 1] = temp

    const updatedShortcuts = shortcuts.map((item, index) => ({
      ...item,
      position: index,
    }))

    const updatedConfig = {
      ...config,
      shortcuts: updatedShortcuts,
    }

    setConfig(updatedConfig)
    setIsSaving(true)

    try {
      await saveConfig(updatedConfig)
      displaySnackbar(t('home-screen:messages.shortcutOrderUpdated'), false)
    } catch (error) {
      displaySnackbar(
        error.response?.data?.message || t('home-screen:messages.shortcutOrderUpdateError'),
        true,
      )
      fetchConfig()
    } finally {
      setIsSaving(false)
    }
  }

  const handleMoveShortcutDown = async (currentIndex) => {
    const shortcuts = [...(config.shortcuts || [])]
    if (currentIndex === shortcuts.length - 1) return

    const temp = shortcuts[currentIndex]
    shortcuts[currentIndex] = shortcuts[currentIndex + 1]
    shortcuts[currentIndex + 1] = temp

    const updatedShortcuts = shortcuts.map((item, index) => ({
      ...item,
      position: index,
    }))

    const updatedConfig = {
      ...config,
      shortcuts: updatedShortcuts,
    }

    setConfig(updatedConfig)
    setIsSaving(true)

    try {
      await saveConfig(updatedConfig)
      displaySnackbar(t('home-screen:messages.shortcutOrderUpdated'), false)
    } catch (error) {
      displaySnackbar(
        error.response?.data?.message || t('home-screen:messages.shortcutOrderUpdateError'),
        true,
      )
      fetchConfig()
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleService = async (serviceId) => {
    const services = [...(config.services || [])]
    const existingIndex = services.findIndex((s) => s.bookingType === serviceId)

    let updatedServices
    if (existingIndex >= 0) {
      // Remove service
      updatedServices = services.filter((s) => s.bookingType !== serviceId)
      // Re-index positions
      updatedServices = updatedServices.map((s, idx) => ({ ...s, position: idx }))
    } else {
      // Add service
      updatedServices = [
        ...services,
        {
          bookingType: serviceId,
          position: services.length,
        },
      ]
    }

    const updatedConfig = {
      ...config,
      services: updatedServices,
    }

    setConfig(updatedConfig)
    setIsSaving(true)

    try {
      await saveConfig(updatedConfig)
      displaySnackbar(t('home-screen:messages.serviceUpdated'), false)
    } catch (error) {
      displaySnackbar(
        error.response?.data?.message || t('home-screen:messages.serviceUpdateError'),
        true,
      )
      fetchConfig()
    } finally {
      setIsSaving(false)
    }
  }

  const handleMoveServiceUp = async (currentIndex) => {
    if (currentIndex === 0) return

    const services = [...(config.services || [])]
    const temp = services[currentIndex]
    services[currentIndex] = services[currentIndex - 1]
    services[currentIndex - 1] = temp

    const updatedServices = services.map((item, index) => ({
      ...item,
      position: index,
    }))

    const updatedConfig = {
      ...config,
      services: updatedServices,
    }

    setConfig(updatedConfig)
    setIsSaving(true)

    try {
      await saveConfig(updatedConfig)
      displaySnackbar(t('home-screen:messages.serviceOrderUpdated'), false)
    } catch (error) {
      displaySnackbar(
        error.response?.data?.message || t('home-screen:messages.serviceOrderUpdateError'),
        true,
      )
      fetchConfig()
    } finally {
      setIsSaving(false)
    }
  }

  const handleMoveServiceDown = async (currentIndex) => {
    const services = [...(config.services || [])]
    if (currentIndex === services.length - 1) return

    const temp = services[currentIndex]
    services[currentIndex] = services[currentIndex + 1]
    services[currentIndex + 1] = temp

    const updatedServices = services.map((item, index) => ({
      ...item,
      position: index,
    }))

    const updatedConfig = {
      ...config,
      services: updatedServices,
    }

    setConfig(updatedConfig)
    setIsSaving(true)

    try {
      await saveConfig(updatedConfig)
      displaySnackbar(t('home-screen:messages.serviceOrderUpdated'), false)
    } catch (error) {
      displaySnackbar(
        error.response?.data?.message || t('home-screen:messages.serviceOrderUpdateError'),
        true,
      )
      fetchConfig()
    } finally {
      setIsSaving(false)
    }
  }

  const saveConfig = async (updatedConfig) => {
    const data = {
      buildingId: Number(buildingId),
      buildingImageEnabled: updatedConfig.buildingImageEnabled || false,
      buildingImageFile: updatedConfig.buildingImageFile || null,
      buildingImageFadeEnabled: updatedConfig.buildingImageFadeEnabled || false,
      shortcutsEnabled: updatedConfig.shortcutsEnabled || false,
      shortcuts: updatedConfig.shortcuts || [],
      upcomingEventsEnabled: updatedConfig.upcomingEventsEnabled || false,
      announcementsEnabled: updatedConfig.announcementsEnabled || false,
      residentTasksEnabled: updatedConfig.residentTasksEnabled || false,
      buildingEssentialsEnabled: updatedConfig.buildingEssentialsEnabled || false,
      buildingEssentials: updatedConfig.buildingEssentials || [],
      servicesEnabled: updatedConfig.servicesEnabled || false,
      services: updatedConfig.services || [],
      chatEnabled: updatedConfig.chatEnabled || false,
      amenitiesEnabled: updatedConfig.amenitiesEnabled || false,
      amenities: updatedConfig.amenities || [],
      neighborhoodPerksEnabled: updatedConfig.neighborhoodPerksEnabled || false,
      neighborhoodPerks: updatedConfig.neighborhoodPerks || [],
      transportEnabled: updatedConfig.transportEnabled || false,
      positions: updatedConfig.positions || [],
    }

    await api.updateHomepageConfig(data)
  }

  const getWidgetDisplayName = (widget) => {
    return WIDGET_LABELS[widget] || widget
  }

  useEffect(() => {
    if (activeBuilding?.id && (!buildingId || Number(buildingId) !== activeBuilding.id)) {
      navigate(`/dashboard/building-dashboard/home-screen/${activeBuilding.id}/legacy`, {
        replace: true,
      })
    }
  }, [activeBuilding, buildingId, navigate])

  useEffect(() => {
    if (buildingId) {
      fetchConfig()
    }
  }, [buildingId])

  if (redirect) return redirect

  if (isLoading) {
    return (
      <div className="home-screen-config">
        <Loader />
      </div>
    )
  }

  if (!config) {
    return (
      <div className="home-screen-config">
        <div className="home-screen-config__empty">{t('home-screen:labels.noConfigMessage')}</div>
      </div>
    )
  }

  const enabledWidgets = getEnabledWidgets()
  const sortedPositions = (config.positions || []).sort((a, b) => a.position - b.position)
  const isShortcutsCapReached = (config.shortcuts || []).length >= MAX_ENABLED_SHORTCUTS

  return (
    <div className="home-screen-config">
      <div className="home-screen-config__header">
        <h2>{t('home-screen:page.title')}</h2>
        <ToggleSwitch
          checked={false}
          falsyLabel={t('home-screen:page.designToggle.old')}
          truthyLabel={t('home-screen:page.designToggle.new')}
          onChange={handleUIToggle}
        />
      </div>
      <div className="home-screen-config__content">
        <div className="home-screen-config__modules">
          <div className="home-screen-config__section-header">
            <h3>{t('home-screen:labels.activeWidgetsHeader')}</h3>
            <p>{t('home-screen:labels.arrowsHint')}</p>
          </div>

          <div className="home-screen-config__module-list">
            {sortedPositions.map((position, index) => {
              const widget = position.widget
              const isEnabled = config[`${widget}Enabled`]
              const isBuildingImage = widget === 'buildingImage'
              const isFirst = index === 0
              const isLast = index === sortedPositions.length - 1
              const canMoveUp = !isFirst && !(isBuildingImage === false && index === 1)
              const canMoveDown = !isLast && !isBuildingImage

              return (
                <div
                  key={widget}
                  className={`home-screen-config__module-item ${
                    isEnabled ? 'active' : 'inactive'
                  } ${isBuildingImage ? 'locked' : ''}`}
                >
                  <div className="home-screen-config__module-controls">
                    <button
                      className="home-screen-config__arrow-btn"
                      onClick={() => handleMoveUp(index)}
                      disabled={!canMoveUp || isSaving}
                      title={t('home-screen:labels.moveUpTitle')}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 4l-6 6h12z" />
                      </svg>
                    </button>
                    <button
                      className="home-screen-config__arrow-btn"
                      onClick={() => handleMoveDown(index)}
                      disabled={!canMoveDown || isSaving}
                      title={t('home-screen:labels.moveDownTitle')}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 12l6-6H2z" />
                      </svg>
                    </button>
                  </div>
                  <div className="home-screen-config__module-info">
                    <span className="home-screen-config__module-name">
                      {getWidgetDisplayName(widget)}
                      {isBuildingImage && isEnabled && (
                        <span className="home-screen-config__locked-badge">
                          {t('home-screen:labels.alwaysFirstBadge')}
                        </span>
                      )}
                    </span>
                    <span className="home-screen-config__module-position">
                      {t('home-screen:labels.positionLabel', { position: position.position + 1 })}
                    </span>
                  </div>
                  <div className="home-screen-config__module-toggle">
                    <label className="home-screen-config__switch">
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => handleToggleWidget(widget)}
                        disabled={isSaving}
                      />
                      <span className="home-screen-config__slider"></span>
                    </label>
                    <span className="home-screen-config__toggle-label">
                      {isEnabled
                        ? t('home-screen:labels.visibleLabel')
                        : t('home-screen:labels.hiddenLabel')}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>

          {Object.keys(WIDGET_LABELS).filter((widget) => !enabledWidgets.includes(widget)).length >
            0 && (
            <div className="home-screen-config__disabled-widgets">
              <h4>{t('home-screen:labels.availableWidgetsHeader')}</h4>
              <p className="home-screen-config__disabled-subtitle">
                {t('home-screen:labels.widgetsToggleHint')}
              </p>
              <div className="home-screen-config__disabled-list">
                {Object.keys(WIDGET_LABELS)
                  .filter((widget) => !enabledWidgets.includes(widget))
                  .map((widget) => (
                    <div key={widget} className="home-screen-config__disabled-item">
                      <span className="home-screen-config__disabled-name">
                        {getWidgetDisplayName(widget)}
                      </span>
                      <div className="home-screen-config__module-toggle">
                        <label className="home-screen-config__switch">
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={() => handleToggleWidget(widget)}
                            disabled={isSaving}
                          />
                          <span className="home-screen-config__slider"></span>
                        </label>
                        <span className="home-screen-config__toggle-label">
                          {t('home-screen:labels.hiddenLabel')}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Shortcuts Configuration */}
          {config.shortcutsEnabled && (
            <div className="home-screen-config__shortcuts-section">
              <div className="home-screen-config__section-header">
                <h3>{t('home-screen:labels.shortcutsConfigurationHeader')}</h3>
                <p>{t('home-screen:labels.shortcutsManageHint')}</p>
              </div>

              <div className="home-screen-config__shortcuts-list">
                {(config.shortcuts || [])
                  .sort((a, b) => a.position - b.position)
                  .map((shortcut, index) => {
                    const isFirst = index === 0
                    const isLast = index === (config.shortcuts || []).length - 1

                    return (
                      <div key={shortcut.type} className="home-screen-config__shortcut-item active">
                        <div className="home-screen-config__module-controls">
                          <button
                            className="home-screen-config__arrow-btn"
                            onClick={() => handleMoveShortcutUp(index)}
                            disabled={isFirst || isSaving}
                            title={t('home-screen:labels.moveUpTitle')}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                              <path d="M8 4l-6 6h12z" />
                            </svg>
                          </button>
                          <button
                            className="home-screen-config__arrow-btn"
                            onClick={() => handleMoveShortcutDown(index)}
                            disabled={isLast || isSaving}
                            title={t('home-screen:labels.moveDownTitle')}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                              <path d="M8 12l6-6H2z" />
                            </svg>
                          </button>
                        </div>
                        <div className="home-screen-config__module-info">
                          <span className="home-screen-config__module-name">
                            {SHORTCUT_LABELS[shortcut.type] || shortcut.type}
                          </span>
                          <span className="home-screen-config__module-position">
                            {t('home-screen:labels.positionLabel', {
                              position: shortcut.position + 1,
                            })}
                          </span>
                        </div>
                        <div className="home-screen-config__module-toggle">
                          <label className="home-screen-config__switch">
                            <input
                              type="checkbox"
                              checked={true}
                              onChange={() => handleToggleShortcut(shortcut.type)}
                              disabled={isSaving}
                            />
                            <span className="home-screen-config__slider"></span>
                          </label>
                          <span className="home-screen-config__toggle-label">
                            {t('home-screen:labels.visibleLabel')}
                          </span>
                        </div>
                      </div>
                    )
                  })}
              </div>

              {/* Available Shortcuts */}
              {ALL_SHORTCUT_TYPES.filter(
                (type) => !(config.shortcuts || []).some((s) => s.type === type),
              ).length > 0 && (
                <div className="home-screen-config__disabled-shortcuts">
                  <h4>{t('home-screen:labels.availableShortcutsHeader')}</h4>
                  <p className="home-screen-config__disabled-subtitle">
                    {isShortcutsCapReached
                      ? SHORTCUTS_CAP_HINT
                      : t('home-screen:labels.shortcutsToggleHint')}
                  </p>
                  <div className="home-screen-config__disabled-list">
                    {ALL_SHORTCUT_TYPES.filter(
                      (type) => !(config.shortcuts || []).some((s) => s.type === type),
                    ).map((type) => (
                      <div key={type} className="home-screen-config__disabled-item">
                        <span className="home-screen-config__disabled-name">
                          {SHORTCUT_LABELS[type]}
                        </span>
                        <div className="home-screen-config__module-toggle">
                          <Tooltip
                            disabled={!isShortcutsCapReached}
                            trigger={
                              <label className="home-screen-config__switch">
                                <input
                                  type="checkbox"
                                  checked={false}
                                  onChange={() => handleToggleShortcut(type)}
                                  disabled={isSaving || isShortcutsCapReached}
                                />
                                <span className="home-screen-config__slider"></span>
                              </label>
                            }
                          >
                            {SHORTCUTS_CAP_HINT}
                          </Tooltip>
                          <span className="home-screen-config__toggle-label">
                            {t('home-screen:labels.hiddenLabel')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Services Configuration */}
          {config.servicesEnabled && (
            <div className="home-screen-config__services-section">
              <div className="home-screen-config__section-header">
                <h3>{t('home-screen:labels.servicesConfigurationHeader')}</h3>
                <p>{t('home-screen:labels.servicesManageHint')}</p>
              </div>

              <div className="home-screen-config__services-list">
                {(config.services || [])
                  .sort((a, b) => a.position - b.position)
                  .map((service, index) => {
                    const isFirst = index === 0
                    const isLast = index === (config.services || []).length - 1
                    const serviceData = service.data

                    return (
                      <div
                        key={service.bookingType}
                        className="home-screen-config__service-item active"
                      >
                        <div className="home-screen-config__module-controls">
                          <button
                            className="home-screen-config__arrow-btn"
                            onClick={() => handleMoveServiceUp(index)}
                            disabled={isFirst || isSaving}
                            title={t('home-screen:labels.moveUpTitle')}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                              <path d="M8 4l-6 6h12z" />
                            </svg>
                          </button>
                          <button
                            className="home-screen-config__arrow-btn"
                            onClick={() => handleMoveServiceDown(index)}
                            disabled={isLast || isSaving}
                            title={t('home-screen:labels.moveDownTitle')}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                              <path d="M8 12l6-6H2z" />
                            </svg>
                          </button>
                        </div>
                        <div className="home-screen-config__module-info">
                          <span className="home-screen-config__module-name">
                            {serviceData?.name ||
                              t('home-screen:labels.serviceFallbackName', {
                                bookingType: service.bookingType,
                              })}
                          </span>
                          <span className="home-screen-config__module-position">
                            {t('home-screen:labels.positionLabel', {
                              position: service.position + 1,
                            })}
                          </span>
                        </div>
                        <div className="home-screen-config__module-toggle">
                          <label className="home-screen-config__switch">
                            <input
                              type="checkbox"
                              checked={true}
                              onChange={() => handleToggleService(service.bookingType)}
                              disabled={isSaving}
                            />
                            <span className="home-screen-config__slider"></span>
                          </label>
                          <span className="home-screen-config__toggle-label">
                            {t('home-screen:labels.visibleLabel')}
                          </span>
                        </div>
                      </div>
                    )
                  })}
              </div>

              {/* Note about available services */}
              <div className="home-screen-config__services-note">
                <p>
                  <strong>{t('home-screen:labels.noteLabel')}</strong>{' '}
                  {t('home-screen:labels.servicesNoteBody')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {isSaving && (
        <div className="home-screen-config__saving-overlay">
          <Loader />
          <span>{t('home-screen:labels.savingChanges')}</span>
        </div>
      )}
    </div>
  )
}

HomeScreen.propTypes = {
  buildings: PropTypes.array,
  activeBuilding: PropTypes.object,
}

export default connect((state) => ({
  buildings: selectBuildingsForFilter(state),
  activeBuilding: selectActiveBuilding(state),
}))(HomeScreen)
